import React, { useState, useEffect } from 'react';
import { aboutData, contactInfo } from '../data';
import { Shield, Award, MapPin, Phone, X, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import ScrollReveal from './ScrollReveal';

const fallbackGallery = [
  {
    id: "1",
    title: { da: "Nyanlagt fliseterrasse" },
    category: { da: "Flisearbejde" },
    image: ["https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/4ff0ce2b-412b-4fcd-80b3-c9745925ecdf.jpg"]
  },
  {
    id: "2",
    title: { da: "Klipning og pleje af hæk" },
    category: { da: "Havepleje" },
    image: ["https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/c8bcef9a-11a3-4edc-8fee-07b19d27e5ee.jpg"]
  },
  {
    id: "3",
    title: { da: "Smuk indkørsel og forhave" },
    category: { da: "Anlæg" },
    image: ["https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/4817d1f4-a80f-4dbe-aca5-664c58aee383.jpg"]
  },
  {
    id: "4",
    title: { da: "Traditionelt stendige i Midt- og Vestjylland" },
    category: { da: "Sten & Mur" },
    image: ["https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/f3249b46-91a9-4181-8a96-711775b88478.jpg"]
  },
  {
    id: "5",
    title: { da: "Eksklusiv træterrasse" },
    category: { da: "Træarbejde" },
    image: ["https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/24fc65a3-4e41-461f-90e2-cbe72c614767.jpg"]
  },
  {
    id: "6",
    title: { da: "Lasse i gang med havearbejdet" },
    category: { da: "Profil" },
    image: ["https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/5a380c33-c297-4e16-b3cd-5a01f4d1485b.jpg"]
  }
];

export default function About() {
  const valueIcons = [MapPin, Shield, Award];
  const [galleryItems, setGalleryItems] = useState(fallbackGallery);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/gallery')
      .then(res => {
        if (!res.ok) throw new Error('Kunne ikke hente galleridata');
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setGalleryItems(data);
        }
      })
      .catch(err => {
        console.warn('Fejl ved hentning af galleri fra CMS, bruger fallback:', err);
      });
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Get unique categories
  const categories = ['Alle', ...new Set(galleryItems.map(item => item.category?.da || item.category).filter(Boolean))];

  // Filtered items
  const filteredItems = selectedCategory === 'Alle'
    ? galleryItems
    : galleryItems.filter(item => (item.category?.da || item.category) === selectedCategory);

  const openLightbox = (index) => {
    // Find index in the filtered list
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const showPrev = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev === 0 ? filteredItems.length - 1 : prev - 1));
  };

  const showNext = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev === filteredItems.length - 1 ? 0 : prev + 1));
  };

  // Handle keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') setLightboxIndex((prev) => (prev === 0 ? filteredItems.length - 1 : prev - 1));
      if (e.key === 'ArrowRight') setLightboxIndex((prev) => (prev === filteredItems.length - 1 ? 0 : prev + 1));
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, filteredItems]);

  return (
    <section id="om-mig" className="py-24 bg-white border-t border-stone-100 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Manden bag maskinen Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-24">
          
          {/* Left Column: Image with details */}
          <div className="lg:col-span-6">
            <ScrollReveal variant="fade-right" duration={800}>
              <div className="relative">
                <img
                  src={aboutData.image}
                  alt={aboutData.title}
                  className="w-full h-auto object-cover aspect-[4/5] rounded-2xl shadow-md border border-stone-100"
                />
                {/* Overlay badge with local text */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md border border-stone-200 p-5 rounded-xl shadow-lg">
                  <p className="text-stone-900 text-xs font-bold uppercase tracking-wider">
                    Manden bag maskinen
                  </p>
                  <h4 className="text-stone-900 font-bold text-base mt-1 uppercase tracking-wider">
                    {aboutData.name}
                  </h4>
                  <p className="text-stone-500 text-sm mt-0.5">
                    Ejer & Anlægsgartner
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Right Column: Bio & Values */}
          <div className="lg:col-span-6">
            <ScrollReveal variant="fade-left" duration={800}>
              <span className="text-stone-900 text-sm font-semibold tracking-wider uppercase">
                Mød Lasse
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 mt-2 mb-6 tracking-wider uppercase">
                Gartneren fra Thy
              </h2>

              {/* Bio Paragraphs */}
              <div className="space-y-4 text-stone-600 text-base leading-relaxed mb-8">
                {aboutData.bio.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              {/* Values List */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t border-stone-200">
                {aboutData.values.map((value, index) => {
                  const IconComponent = valueIcons[index] || Shield;
                  return (
                    <div key={index} className="flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-stone-900">
                          <IconComponent className="h-5 w-5" />
                        </div>
                        <h4 className="font-bold text-stone-900 text-xs uppercase tracking-wider">
                          {value.title}
                        </h4>
                      </div>
                      <p className="text-stone-500 text-xs leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* CTA */}
              <div className="mt-8 flex flex-wrap items-center gap-3 sm:gap-4">
                <button
                  onClick={() => scrollToSection('kontakt')}
                  className="bg-stone-900 hover:bg-stone-800 text-white font-semibold px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl transition-all shadow-md hover:-translate-y-0.5 cursor-pointer text-xs sm:text-sm uppercase tracking-wider"
                >
                  Tag en uforpligtende snak
                </button>
                <a
                  href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`}
                  className="flex items-center gap-2 text-stone-600 hover:text-stone-900 font-semibold px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl transition-colors text-xs sm:text-sm uppercase tracking-wider"
                >
                  <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-stone-900" />
                  {contactInfo.phone}
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>

        {/* Gallery Section */}
        <div className="pt-16 border-t border-stone-100">
          <ScrollReveal variant="fade-up" duration={800}>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-stone-900 text-xs font-bold uppercase tracking-widest">
                Udført arbejde
              </span>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-stone-900 mt-2 mb-4 uppercase tracking-wider">
                Mit arbejde i billeder
              </h3>
              <p className="text-stone-600 text-sm sm:text-base">
                Se et udvalg af mine projekter rundt omkring i Midt- og Vestjylland – fra flisearbejde og stendiger til komplet havepleje og vedligeholdelse.
              </p>
            </div>
          </ScrollReveal>

          {/* Category Filters */}
          <ScrollReveal variant="fade-in" duration={800} delay={100}>
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-stone-900 text-white shadow-sm'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200/80'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </ScrollReveal>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item, index) => {
              const itemTitle = item.title?.da || item.title;
              const itemCategory = item.category?.da || item.category;
              const imageUrl = Array.isArray(item.image) ? item.image[0] : item.image;

              return (
                <ScrollReveal
                  key={item.id || index}
                  variant="scale-up"
                  duration={600}
                  delay={(index % 3) * 100}
                >
                  <div
                    onClick={() => openLightbox(index)}
                    className="group relative overflow-hidden rounded-2xl border border-stone-200/60 shadow-sm bg-stone-50 aspect-[4/3] cursor-pointer"
                  >
                    <img
                      src={imageUrl}
                      alt={itemTitle}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>

      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-stone-950/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-stone-400 hover:text-white transition-colors p-2 rounded-full bg-stone-900/50 hover:bg-stone-800/50 z-50 cursor-pointer"
            aria-label="Luk galleri"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Prev Button */}
          <button
            onClick={showPrev}
            className="absolute left-4 sm:left-6 text-stone-400 hover:text-white transition-colors p-3 rounded-full bg-stone-900/50 hover:bg-stone-800/50 z-50 cursor-pointer"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="h-6 w-6 sm:h-8 sm:w-8" />
          </button>

          {/* Next Button */}
          <button
            onClick={showNext}
            className="absolute right-4 sm:right-6 text-stone-400 hover:text-white transition-colors p-3 rounded-full bg-stone-900/50 hover:bg-stone-800/50 z-50 cursor-pointer"
            aria-label="Næste billede"
          >
            <ChevronRight className="h-6 w-6 sm:h-8 sm:w-8" />
          </button>

          {/* Main Image Container */}
          <div
            className="relative max-w-5xl max-h-[75vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={
                Array.isArray(filteredItems[lightboxIndex].image)
                  ? filteredItems[lightboxIndex].image[0]
                  : filteredItems[lightboxIndex].image
              }
              alt={filteredItems[lightboxIndex].title?.da || filteredItems[lightboxIndex].title}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl border border-stone-800"
            />
            
            {/* Caption */}
            <div className="text-center mt-4 text-white max-w-xl px-4">
              <span className="text-stone-400 text-xs font-bold uppercase tracking-wider">
                {filteredItems[lightboxIndex].category?.da || filteredItems[lightboxIndex].category}
              </span>
              <h4 className="font-bold text-base sm:text-lg uppercase tracking-wider mt-1">
                {filteredItems[lightboxIndex].title?.da || filteredItems[lightboxIndex].title}
              </h4>
              <p className="text-stone-500 text-xs mt-2">
                {lightboxIndex + 1} af {filteredItems.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
